import { watch, type Ref } from 'vue';
import type { NavigationGuardReturn, RouteLocationNormalized } from 'vue-router';

export interface GuardDeps {
    auth: { isLoading: Ref<boolean>; isAuthenticated: Ref<boolean> };
    /** Current tenant, or null when none has been confirmed this session. */
    readTenantId: () => string | null;
}

/**
 * Resolves once Auth0 has finished deciding whether there is a session.
 *
 * Copied from `@auth0/auth0-vue`'s own `createGuardHandler`. Deciding before
 * `isLoading` settles races the plugin's redirect-callback handling, and the loser
 * is the deep link: the guard sees "not authenticated", bounces to /login, and the
 * route the user actually asked for is thrown away.
 */
function whenSettled(isLoading: Ref<boolean>): Promise<void> {
    if (!isLoading.value) return Promise.resolve();

    return new Promise((resolve) => {
        const stop = watch(isLoading, (loading) => {
            if (!loading) {
                stop();
                resolve();
            }
        });
    });
}

/**
 * Route guard for the shell.
 *
 * Takes its dependencies as plain refs rather than calling `useAuth0()`, because
 * `useAuth0()` is `inject()` under the hood and returns undefined outside a setup
 * context — a guard in its own module cannot use it. Passing them in also makes
 * this unit-testable with no router and no Auth0.
 *
 * Written by hand rather than using `@auth0/auth0-vue`'s `createAuthGuard`, which
 * jumps straight to `loginWithRedirect` with options frozen at construction time.
 * Sign-in here needs `authorizationParams: { tenant_id }` for a tenant the user has
 * not chosen yet, so an unauthenticated visitor has to reach the picker first.
 */
export function createAppGuard({ auth, readTenantId }: GuardDeps) {
    return async function appGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
        // The Auth0 callback lands on '/' carrying ?code&state while the plugin's
        // __checkSession is still in flight. Never interfere with that navigation.
        if (to.query.code && to.query.state) return true;

        if (to.meta.requiresAuth === false) return true;

        await whenSettled(auth.isLoading);

        const tenantId = readTenantId();
        if (!auth.isAuthenticated.value || !tenantId) {
            return { name: 'login', query: { redirect: to.fullPath } };
        }

        // Tenant gate. The nav and the home index already hide what this tenant
        // may not use, but both only hide a link — this is what stops a bookmark
        // or a pasted URL from opening an app against the wrong customer's graph.
        //
        // Redirects to the index carrying the app id rather than answering 404,
        // so the tester is told the app exists but not for this tenant. A silent
        // bounce to home reads as a broken link.
        const allowed = to.meta.tenants as string[] | undefined;
        if (allowed && !allowed.includes(tenantId)) {
            return { name: 'home', query: { unavailable: String(to.meta.appId ?? to.name) } };
        }

        return true;
    };
}
